import {
  ONE_WAY,
  RETURN,
  MULTI_DESTINATION,
} from '@web-platform/shared-universal-constants/src/tripTypes';
import { ReadOnlyMap } from '@web-platform/shared-universal-utils-frontend/src/helpers/ReadOnlyMap';

import { translate } from '../translate';

import type { TripType } from '@web-platform/shared-universal-constants/src/tripTypes';

// String to TripType mapping (shared by both Acorn and Banana)
const stringTripTypePairs: Array<[string, TripType]> = [
  ['one-way', ONE_WAY],
  ['return', RETURN],
  ['multi-destination', MULTI_DESTINATION],
];

const stringToTripTypeMap = ReadOnlyMap<string, TripType>(stringTripTypePairs);

// TripType to string mapping (for Banana's getDashedTripType)
const tripTypeStringPairs: Array<[TripType, string]> = [
  [ONE_WAY, 'one-way'],
  [RETURN, 'return'],
  [MULTI_DESTINATION, 'multi-destination'],
];

const tripTypeToStringMap = ReadOnlyMap<TripType, string>(tripTypeStringPairs);

// TripType to translation mapping (for Acorn's formatTripType)
const tripTypeTranslations: Array<[TripType, string]> = [
  [ONE_WAY, translate('ktxtSearchControls_OneWay')],
  [RETURN, translate('ktxtSearchControls_Return')],
  [MULTI_DESTINATION, translate('ktxtSearchControls_MultiCity')],
];

let tripTypeTranslationsMap: ReturnType<typeof ReadOnlyMap<TripType, string>>;

/**
 * Converts string trip type to TripType constant
 * Unified implementation for both Acorn and Banana
 * @param {string} tripType - The trip type string ('one-way', 'return', 'multi-destination')
 * @returns {TripType} The TripType constant
 */
export const convertFromDashedTripType = (tripType: string): TripType => {
  const trip = stringToTripTypeMap.getOrError(tripType, 'Unable to determine trip type.');

  if (trip instanceof Error) {
    throw trip;
  }

  return trip;
};

/**
 * Converts TripType constant back to dashed string format
 * Originally from Banana implementation
 * @param {TripType} tripType - The TripType constant
 * @returns {string} The dashed string format ('one-way', 'return', 'multi-destination')
 */
export const convertToDashedTripType = (tripType: TripType): string => {
  const trip = tripTypeToStringMap.getOrError(tripType, 'Unable to determine trip type.');

  if (trip instanceof Error) {
    throw trip;
  }

  return trip;
};

/**
 * Formats TripType with translated text
 * Originally from Acorn implementation
 * @param {TripType} tripType - The TripType constant
 * @returns {string} The translated trip type text
 */
export const formatTripType = (tripType: TripType) => {
  if (!tripTypeTranslationsMap) {
    tripTypeTranslationsMap = ReadOnlyMap<TripType, string>(tripTypeTranslations);
  }

  return tripTypeTranslationsMap.getOrDefault(tripType, tripType || '');
};
